import React from 'react'
import { applyPolyfillForComponent } from '../utils'
import type { ILayoutDoneService, Experiments } from '@wix/thunderbolt-symbols'
import type { PresetPolyfillData } from '../types'

interface UseBuilderComponentPolyfillOptions {
	builderComponentId: string
	getPresetsPolyfillData: () => Record<string, PresetPolyfillData>
	layoutDoneService?: ILayoutDoneService
	experiments: Experiments
}

/**
 * Hook to apply presets polyfill for builder components.
 *
 * **Editor (DS)**: Waits for renderDone to ensure CSS is fully applied before running polyfill.
 * **Public Viewer**: Applies polyfill immediately (styles are already in SSR'd HTML).
 */
export const useBuilderComponentPolyfill = ({
	builderComponentId,
	getPresetsPolyfillData,
	layoutDoneService,
	experiments,
}: UseBuilderComponentPolyfillOptions): void => {
	React.useEffect(() => {
		const applyPresetsPolyfill = () => {
			try {
				const processedPolyfillData = getPresetsPolyfillData()
				applyPolyfillForComponent(builderComponentId, processedPolyfillData)
			} catch (error) {
				console.error('[Polyfill] Failed to apply polyfill for builderComponentId:', builderComponentId, error)
			}
		}

		const isEditor = process.env.PACKAGE_NAME === 'thunderbolt-ds'

		if (isEditor && layoutDoneService) {
			// Editor path: Register to render done to apply polyfill after all CSS is rendered
			const unsubscribe = layoutDoneService.registerToRenderDone(
				() => {
					applyPresetsPolyfill()
				},
				{ once: true }
			)

			return () => {
				if (unsubscribe) {
					layoutDoneService.unregisterToLayoutDone(unsubscribe)
				}
			}
		} else {
			// Public viewer path: Apply immediately (styles are already in SSR'd HTML)
			applyPresetsPolyfill()
		}
	}, [builderComponentId, getPresetsPolyfillData, layoutDoneService, experiments])
}
